import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Performance API functions
export const performanceService = {
  // Get all performance reviews
  getReviews: async (params = {}) => {
    try {
      const response = await api.get('/performance/reviews', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching performance reviews:', error);
      throw error;
    }
  },

  // Get performance review by ID
  getReview: async (id) => {
    try {
      const response = await api.get(`/performance/reviews/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching performance review:', error);
      throw error;
    }
  },

  // Create new performance review
  createReview: async (reviewData) => {
    try {
      const response = await api.post('/performance/reviews', reviewData);
      return response.data;
    } catch (error) {
      console.error('Error creating performance review:', error);
      throw error;
    }
  },

  // Update performance review
  updateReview: async (id, reviewData) => {
    try {
      const response = await api.put(`/performance/reviews/${id}`, reviewData);
      return response.data;
    } catch (error) {
      console.error('Error updating performance review:', error);
      throw error;
    }
  },

  // Add performance criteria
  addCriteria: async (reviewId, criteriaData) => {
    try {
      const response = await api.post(`/performance/reviews/${reviewId}/criteria`, criteriaData);
      return response.data;
    } catch (error) {
      console.error('Error adding performance criteria:', error);
      throw error;
    }
  },

  // Update performance criteria
  updateCriteria: async (id, criteriaData) => {
    try {
      const response = await api.put(`/performance/criteria/${id}`, criteriaData);
      return response.data;
    } catch (error) {
      console.error('Error updating performance criteria:', error);
      throw error;
    }
  },

  // Complete performance review
  completeReview: async (id, reviewData) => {
    try {
      const response = await api.patch(`/performance/reviews/${id}/complete`, reviewData);
      return response.data;
    } catch (error) {
      console.error('Error completing performance review:', error);
      throw error;
    }
  },

  // Employee sign performance review
  signReview: async (id, signature) => {
    try {
      const response = await api.patch(`/performance/reviews/${id}/sign`, { signature });
      return response.data;
    } catch (error) {
      console.error('Error signing performance review:', error);
      throw error;
    }
  },

  // Reviewer sign performance review
  reviewerSignReview: async (id, signature) => {
    try {
      const response = await api.patch(`/performance/reviews/${id}/reviewer-sign`, { signature });
      return response.data;
    } catch (error) {
      console.error('Error signing performance review:', error);
      throw error;
    }
  },

  // Get my performance reviews (employee view)
  getMyReviews: async (params = {}) => {
    try {
      const response = await api.get('/performance/my-reviews', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching my performance reviews:', error);
      throw error;
    }
  },

  // Get reviews I'm conducting (reviewer view)
  getConductingReviews: async (params = {}) => {
    try {
      const response = await api.get('/performance/conducting', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching conducting reviews:', error);
      throw error;
    }
  },

  // Get performance summary
  getPerformanceSummary: async (params = {}) => {
    try {
      const response = await api.get('/performance/summary', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching performance summary:', error);
      throw error;
    }
  }
};

export default performanceService; 